import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Search, Edit, Plus, UserPlus, Check, X } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { OnlineStatus } from '../components/messages/OnlineStatus';
import { useAuthStore } from '../store/authStore';
import { getConversations, getMessageRequests, respondToMessageRequest } from '../lib/messageService';
import { realtimeService } from '../lib/realtimeService';
import { Conversation, MessageRequest } from '../types';

export const MessagesPage: React.FC = () => {
  const { user } = useAuthStore();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messageRequests, setMessageRequests] = useState<MessageRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'conversations' | 'requests'>('conversations');
  const [searchQuery, setSearchQuery] = useState('');
  const [processingRequests, setProcessingRequests] = useState<Set<string>>(new Set());
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (user) {
      loadData();
      
      // Subscribe to conversation updates
      const unsubscribe = realtimeService.subscribeToUserConversations(
        user.id,
        handleConversationUpdate
      );

      return () => unsubscribe();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const [convs, requests] = await Promise.all([
        getConversations(user.id),
        getMessageRequests(user.id)
      ]);
      setConversations(convs);
      setMessageRequests(requests);
    } catch (error) {
      console.error('Error loading messages data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConversationUpdate = (payload: any) => {
    if (payload.event === 'message_added') {
      // Move conversation to top and update last message
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.id === payload.conversation_id) {
            return {
              ...conv,
              last_message_at: new Date().toISOString(),
              unread_count: conv.unread_count + 1
            };
          }
          return conv;
        });
        
        // Sort by last message time
        return updated.sort((a, b) => 
          new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
        );
      });
    } else {
      // Reload conversations for other events
      loadData();
    }
  };

  const handleMessageRequest = async (requestId: string, action: 'accept' | 'reject') => {
    setProcessingRequests(prev => new Set(prev).add(requestId));
    
    try {
      const conversationId = await respondToMessageRequest(requestId, action);
      
      // Remove from requests
      setMessageRequests(prev => prev.filter(req => req.id !== requestId));
      
      if (action === 'accept' && conversationId) {
        // Reload conversations to show the new one
        const convs = await getConversations(user?.id || '');
        setConversations(convs);
        
        // Navigate to the new conversation
        window.location.href = `/messages/${conversationId}`;
      }
    } catch (error) {
      console.error(`Error ${action}ing message request:`, error);
      alert(`Failed to ${action} message request. Please try again.`);
    } finally {
      setProcessingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      if (!dateString) {
        return 'now';
      }

      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string in formatTimeAgo:', dateString);
        return 'now';
      }

      const now = new Date();
      const diffInMilliseconds = now.getTime() - date.getTime();
      const diffInSeconds = Math.floor(diffInMilliseconds / 1000);

      if (diffInSeconds < 60 || diffInSeconds < 0) {
        return 'now';
      }

      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
      if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;
      
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch (error) {
      console.error('Error formatting time ago:', error, 'for date:', dateString);
      return 'now';
    }
  };

  const getOtherParticipant = (conversation: Conversation) => {
    return conversation.participants.find(p => p.id !== user?.id);
  };

  const filteredConversations = conversations.filter(conv => {
    const otherParticipant = getOtherParticipant(conv);
    return otherParticipant && (
      otherParticipant.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      otherParticipant.displayName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const filteredRequests = messageRequests.filter(req =>
    req.sender.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    req.sender.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 max-w-screen-md">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 max-w-screen-md">
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center">
              <MessageCircle className="h-5 w-5 mr-2" />
              <h1 className="text-lg font-medium">Messages</h1>
            </div>
            <Button variant="ghost" size="sm">
              <Edit className="h-4 w-4" />
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('conversations')}
              className={`flex-1 py-3 px-4 text-sm font-medium ${
                activeTab === 'conversations'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Conversations ({conversations.length})
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 py-3 px-4 text-sm font-medium relative ${
                activeTab === 'requests'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Requests ({messageRequests.length})
              {messageRequests.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {messageRequests.length}
                </span>
              )}
            </button>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${activeTab}...`}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {activeTab === 'conversations' ? (
              filteredConversations.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {filteredConversations.map((conversation) => {
                    const otherParticipant = getOtherParticipant(conversation);
                    if (!otherParticipant) return null;

                    const isOnline = onlineUsers.has(otherParticipant.id);

                    return (
                      <Link
                        key={conversation.id}
                        to={`/messages/${conversation.id}`}
                        className="flex items-center p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="relative">
                          <img
                            src={otherParticipant.profileImage}
                            alt={otherParticipant.username}
                            className="h-12 w-12 rounded-full object-cover mr-4"
                          />
                          {/* Online indicator */}
                          {isOnline && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-gray-900 truncate">
                              {otherParticipant.username}
                            </p>
                            {conversation.last_message && (
                              <span className="text-xs text-gray-500">
                                {formatTimeAgo(conversation.last_message.created_at)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-500 truncate">
                              {conversation.last_message ? (
                                <>
                                  {conversation.last_message.sender_id === user?.id && 'You: '}
                                  {conversation.last_message.message_type === 'image' ? '📷 Photo' :
                                   conversation.last_message.message_type === 'post_share' ? '📝 Shared a post' :
                                   conversation.last_message.content}
                                </>
                              ) : (
                                'Start a conversation'
                              )}
                            </p>
                            {conversation.unread_count > 0 && (
                              <div className="ml-2 bg-purple-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                {conversation.unread_count}
                              </div>
                            )}
                          </div>
                          <OnlineStatus 
                            isOnline={isOnline}
                            className="mt-1"
                          />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">
                    {searchQuery ? 'No conversations found' : 'No messages yet'}
                  </p>
                  <p className="text-sm">
                    {searchQuery 
                      ? 'Try searching for a different name'
                      : 'Start a conversation by visiting someone\'s profile and clicking Message'
                    }
                  </p>
                </div>
              )
            ) : (
              filteredRequests.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {filteredRequests.map((request) => (
                    <div key={request.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start">
                          <img
                            src={request.sender.profileImage}
                            alt={request.sender.username}
                            className="h-12 w-12 rounded-full object-cover mr-4"
                          />
                          <div className="flex-1">
                            <div className="flex items-center">
                              <p className="font-medium text-gray-900 mr-2">
                                {request.sender.username}
                              </p>
                              <span className="text-xs text-gray-500">
                                {formatTimeAgo(request.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {request.message}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex space-x-2 ml-4">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleMessageRequest(request.id, 'accept')}
                            disabled={processingRequests.has(request.id)}
                            isLoading={processingRequests.has(request.id)}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMessageRequest(request.id, 'reject')}
                            disabled={processingRequests.has(request.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <UserPlus className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">
                    {searchQuery ? 'No requests found' : 'No message requests'}
                  </p>
                  <p className="text-sm">
                    {searchQuery 
                      ? 'Try searching for a different name'
                      : 'When someone sends you a message request, it will appear here'
                    }
                  </p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};